import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { API_ENDPOINTS } from '../config/api.config';
import { fetchManagedUsersHierarchyFromApi } from './peopleManagerApi';

const NO_TEAM_LABEL = 'No team';
const COL_RESOURCE = 0;
const COL_TEAM = 1;
const COL_FIRST_DAY = 2;
const ROW_WEEKDAY = 0;
const ROW_DAY_NUM = 1;
const ROW_DATA_START = 2;
const LEGEND_TEXT = 'F = Full day | P = Partial | Grey columns = weekend';

const FILL_FULL = { patternType: 'solid', fgColor: { rgb: 'FFCDD2' } };
const FILL_PARTIAL = { patternType: 'solid', fgColor: { rgb: 'FFF9C4' } };
// Allineato al weekend della UI (~rgba(55,55,55,0.08) su bianco)
const FILL_WEEKEND = { patternType: 'solid', fgColor: { rgb: 'E8E8E8' } };
const FONT_WEEKEND = { color: { rgb: '616161' } };
const FONT_HEADER = { bold: true };

/**
 * @param {number} year
 * @param {number} month - 1-12
 * @returns {Set<number>} day numbers (1-based) that fall on Sat/Sun
 */
export function getWeekendDayNumbers(year, month) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const weekend = new Set();
  for (let d = 1; d <= daysInMonth; d += 1) {
    const dow = new Date(year, month - 1, d).getDay();
    if (dow === 0 || dow === 6) weekend.add(d);
  }
  return weekend;
}

export function resolveDisplayName(user) {
  if (!user) return '';
  return (
    user.username ||
    user.name ||
    (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '') ||
    user.email ||
    ''
  );
}

/** Direct report row for people-manager views (is_people_manager from backend). */
export function mapDirectReportUser(user) {
  const userName = resolveDisplayName(user);
  return {
    id: user.id,
    name: userName,
    username: userName,
    email: user.email,
    org_role_id: user.org_role_id,
    isPeopleManager: !!user.is_people_manager,
  };
}

export async function fetchDirectReportsByPmId(pmId) {
  const res = await fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${pmId}`);
  if (!res.ok) {
    throw new Error(`Failed to load direct reports for people manager ${pmId}`);
  }
  const data = await res.json();
  return (data.users || []).map(mapDirectReportUser);
}

/** Nested user row for hierarchy search (export + autocomplete). */
export function mapHierarchyUserForSearch(user, rootUserId) {
  return {
    id: user.id,
    name: user.name || user.username,
    username: user.username || user.name,
    email: user.email,
    isPeopleManager: !!user.isPeopleManager,
    peopleManagerId: user.peopleManagerId,
    peopleManagerName: user.peopleManagerName,
    isNested: String(user.peopleManagerId) !== String(rootUserId),
  };
}

/**
 * @param {Array<object>} projectUsers
 * @param {Record<string, string>} teamMembershipByUsername
 */
export function sortMembersForCalendar(projectUsers, teamMembershipByUsername) {
  const membership = teamMembershipByUsername || {};
  const rows = (projectUsers || []).map((user) => {
    const displayName = resolveDisplayName(user);
    const keys = [user.username, displayName, user.email].filter(Boolean);
    let teamName = NO_TEAM_LABEL;
    for (const key of keys) {
      if (membership[key]) {
        teamName = membership[key];
        break;
      }
    }
    return {
      user,
      displayName,
      teamName,
      userId: user?.id != null ? String(user.id) : '',
    };
  });

  rows.sort((a, b) => {
    const teamA = a.teamName === NO_TEAM_LABEL ? '\uffff' : a.teamName;
    const teamB = b.teamName === NO_TEAM_LABEL ? '\uffff' : b.teamName;
    const byTeam = teamA.localeCompare(teamB, undefined, { sensitivity: 'base' });
    if (byTeam !== 0) return byTeam;
    return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
  });

  return rows;
}

/**
 * Loads every user in the people-manager hierarchy below rootPmId (all levels).
 */
export async function fetchManagedUsersHierarchy(rootPmId, rootPmName = 'Direct reports') {
  return fetchManagedUsersHierarchyFromApi(rootPmId, rootPmName);
}

export function sortPeopleManagerUsersForCalendar(managedUsers) {
  const rows = (managedUsers || []).map((u) => ({
    displayName: u.name || u.username || resolveDisplayName(u),
    teamName: u.peopleManagerName || NO_TEAM_LABEL,
    userId: u?.id != null ? String(u.id) : '',
  }));

  rows.sort((a, b) => {
    const byTeam = a.teamName.localeCompare(b.teamName, undefined, { sensitivity: 'base' });
    if (byTeam !== 0) return byTeam;
    return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
  });

  return rows;
}

export function getAbsenceCode(absenceEntry) {
  if (!absenceEntry) return '';
  return absenceEntry.full_day ? 'F' : 'P';
}

function dayColumnIndex(dayNum) {
  return COL_FIRST_DAY + (dayNum - 1);
}

function setCellStyle(worksheet, row, col, style) {
  const addr = XLSX.utils.encode_cell({ r: row, c: col });
  if (!worksheet[addr]) {
    worksheet[addr] = { t: 's', v: '' };
  }
  worksheet[addr].s = { ...(worksheet[addr].s || {}), ...style };
}

/**
 * @returns {string[][]}
 */
export function buildAbsenceCalendarAoA({
  year,
  month,
  members,
  absencesByUserId,
  secondColumnLabel = 'Team',
}) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const weekdayRow = ['Resource', secondColumnLabel];
  const dayNumRow = ['', ''];

  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = new Date(year, month - 1, d);
    weekdayRow.push(date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0));
    dayNumRow.push(String(d));
  }

  const dataRows = (members || []).map((member) => {
    const row = [member.displayName, member.teamName];
    const userAbsences = absencesByUserId?.[member.userId];
    for (let d = 1; d <= daysInMonth; d += 1) {
      const entry = userAbsences?.get?.(d) || null;
      row.push(getAbsenceCode(entry));
    }
    return row;
  });

  const legendRow = [LEGEND_TEXT, ...Array(daysInMonth + 1).fill('')];

  return [weekdayRow, dayNumRow, ...dataRows, legendRow];
}

function getLayoutRows(memberCount) {
  const legendRow = ROW_DATA_START + memberCount;
  return {
    rowWeekday: ROW_WEEKDAY,
    rowDayNum: ROW_DAY_NUM,
    rowDataStart: ROW_DATA_START,
    rowLegend: legendRow,
    lastDataRow: memberCount > 0 ? legendRow - 1 : ROW_DATA_START - 1,
  };
}

/**
 * @param {object} worksheet
 * @param {{ year: number, month: number, members: Array, absencesByUserId: object, weekendDays: Set<number> }} opts
 */
export function applyCalendarCellStyles(worksheet, { year, month, members, absencesByUserId, weekendDays }) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const memberCount = members?.length || 0;
  const { rowWeekday, rowDayNum, rowDataStart, rowLegend, lastDataRow } = getLayoutRows(memberCount);

  setCellStyle(worksheet, rowLegend, COL_RESOURCE, { font: { italic: true } });

  for (let c = COL_RESOURCE; c <= COL_TEAM; c += 1) {
    setCellStyle(worksheet, rowWeekday, c, { font: FONT_HEADER });
    setCellStyle(worksheet, rowDayNum, c, { font: FONT_HEADER });
  }

  for (let d = 1; d <= daysInMonth; d += 1) {
    const col = dayColumnIndex(d);
    setCellStyle(worksheet, rowWeekday, col, { font: FONT_HEADER, alignment: { horizontal: 'center' } });
    setCellStyle(worksheet, rowDayNum, col, { font: FONT_HEADER, alignment: { horizontal: 'center' } });

    const isWeekend = weekendDays.has(d);

    // Intestazioni giorni: solo weekend (la "F" di Friday non è assenza full day)
    for (let row = rowWeekday; row <= rowDayNum; row += 1) {
      if (isWeekend) {
        setCellStyle(worksheet, row, col, {
          fill: FILL_WEEKEND,
          font: FONT_HEADER,
          alignment: { horizontal: 'center' },
        });
      }
    }

    // Righe risorse: F/P = codici assenza
    for (let row = rowDataStart; row <= lastDataRow; row += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      const value = cell?.v;
      const hasAbsence = value === 'F' || value === 'P';

      if (hasAbsence) {
        setCellStyle(worksheet, row, col, {
          fill: value === 'F' ? FILL_FULL : FILL_PARTIAL,
          alignment: { horizontal: 'center' },
        });
      } else if (isWeekend) {
        setCellStyle(worksheet, row, col, {
          fill: FILL_WEEKEND,
          font: FONT_WEEKEND,
          alignment: { horizontal: 'center' },
        });
      }
    }
  }

  (members || []).forEach((member, idx) => {
    const row = rowDataStart + idx;
    setCellStyle(worksheet, row, COL_RESOURCE, { alignment: { horizontal: 'left' } });
    setCellStyle(worksheet, row, COL_TEAM, { alignment: { horizontal: 'left' } });
  });
}

function cleanFileNamePart(name) {
  return (name || 'project').replace(/[^a-zA-Z0-9]/g, '_');
}

function sanitizeSheetName(name, usedNames) {
  let base = (name || 'Project').replace(/[\\/*?:\[\]]/g, '').trim() || 'Project';
  if (base.length > 31) base = base.slice(0, 31);

  let candidate = base;
  let counter = 1;
  while (usedNames.has(candidate)) {
    const suffix = `_${counter}`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    counter += 1;
  }
  usedNames.add(candidate);
  return candidate;
}

function buildAbsenceCalendarWorksheet({
  year,
  month,
  members,
  absencesByUserId,
  secondColumnLabel = 'Team',
}) {
  const weekendDays = getWeekendDayNumbers(year, month);
  const aoa = buildAbsenceCalendarAoA({
    year,
    month,
    members,
    absencesByUserId,
    secondColumnLabel,
  });
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  applyCalendarCellStyles(worksheet, {
    year,
    month,
    members,
    absencesByUserId,
    weekendDays,
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  worksheet['!cols'] = [
    { wch: 28 },
    { wch: 18 },
    ...Array(daysInMonth).fill({ wch: 4 }),
  ];

  return worksheet;
}

export async function fetchProjectCalendarMembers(projectId, year, month) {
  const monthStart = new Date(year, month - 1, 1);

  const [usersRes, teamsRes] = await Promise.all([
    fetch(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${encodeURIComponent(projectId)}&include_full_details=true`),
    fetch(`${API_ENDPOINTS.TEAMS}?project_id=${encodeURIComponent(projectId)}&include_members=true`),
  ]);

  if (!usersRes.ok) {
    throw new Error(`Failed to load users for project ${projectId}`);
  }
  if (!teamsRes.ok) {
    throw new Error(`Failed to load teams for project ${projectId}`);
  }

  const usersData = await usersRes.json();
  const teamsData = await teamsRes.json();

  const validUsers = (usersData.project_users || []).filter((item) => {
    const validityEndDate = item.validity_end_date;
    if (!validityEndDate) return true;
    const endDate = new Date(validityEndDate);
    return endDate >= monthStart;
  });

  const projectUsers = validUsers.map((item) => (item.user ? item.user : item));

  const membership = {};
  (teamsData.teams || []).forEach((team) => {
    (team.members || []).forEach((member) => {
      const user = member.user || member;
      const username = resolveDisplayName(user);
      if (!username) return;
      if (!membership[username]) {
        membership[username] = team.name || 'Team';
      }
    });
  });

  return { projectUsers, teamMembershipByUsername: membership };
}

/**
 * Builds and downloads one workbook with one sheet per project.
 */
export async function downloadAllProjectsAbsenceCalendarWorkbook({
  projects,
  year,
  month,
  absencesByUserId,
  scopeLabel = 'All_projects',
}) {
  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set();

  for (const project of projects || []) {
    const { projectUsers, teamMembershipByUsername } = await fetchProjectCalendarMembers(
      project.id,
      year,
      month
    );
    const members = sortMembersForCalendar(projectUsers, teamMembershipByUsername);
    const worksheet = buildAbsenceCalendarWorksheet({
      year,
      month,
      members,
      absencesByUserId,
    });
    const sheetName = sanitizeSheetName(project.name, usedSheetNames);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  if (workbook.SheetNames.length === 0) {
    throw new Error('No projects to export.');
  }

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  const monthPad = String(month).padStart(2, '0');
  const fileName = `absences_${cleanFileNamePart(scopeLabel)}_${monthPad}${year}.xlsx`;
  saveAs(blob, fileName);
}

/**
 * Builds and downloads the absence calendar workbook.
 */
export function downloadAbsenceCalendarWorkbook({
  projectName,
  scopeLabel,
  year,
  month,
  members,
  absencesByUserId,
  secondColumnLabel = 'Team',
}) {
  const worksheet = buildAbsenceCalendarWorksheet({
    year,
    month,
    members,
    absencesByUserId,
    secondColumnLabel,
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Absences');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  const monthPad = String(month).padStart(2, '0');
  const fileName = `absences_${cleanFileNamePart(scopeLabel || projectName || 'export')}_${monthPad}${year}.xlsx`;
  saveAs(blob, fileName);
}
